import { Box, Grid, Paper, useTheme } from "@mui/material";
import { FC, useMemo } from "react";
import { Link, Outlet } from "react-router-dom";
import { AgentProjectSummary, Permissions } from "../../api/dtos";
import GridActions from "../../components/GridActions";
import SetLinks from "../../components/layout/SetLinks";
import { StyledButton } from "../../components/StyledButton";
import CollapsibleAppTable from "../../components/tables/CollapsibleAppTable";
import type { AppColumn } from "../../components/tables/AppTable.types";
import AgentProjectSummariesContext from "../../contexts/AgentProjectsContext";
import { DefaultProjectCreateLink } from "../../contexts/layouts/LinkContext";
import useAgentProjectSummaries from "../../hooks/data/useAgentProjectSummaries";

function requiredAgentProjectId(row: AgentProjectSummary): number {
    if (row.id === undefined || row.id === null) {
        throw new Error("Agent project summary is missing its ID.");
    }

    return row.id;
}

const useAgentLocationColumns = () => {
    const agentColumns = useMemo(() => {
        const columns: AppColumn<AgentProjectSummary>[] = [
            {
                id: "agentName",
                label: "Agent",
                scalarType: "text",
                accessor: (row) => row.agentName,
                flex: 1,
            },
            {
                id: "location",
                label: "Location",
                scalarType: "text",
                accessor: (row) => row.location,
                flex: 1,
            },
            {
                id: "projectName",
                label: "Project",
                scalarType: "text",
                accessor: (row) => row.projectName,
                flex: 1,
            },
            {
                id: "actions",
                label: "",
                headerLabel: "",
                kind: "action",
                scalarType: "text",
                accessor: (row) => String(row.id ?? ""),
                width: 100,
                cell: ({ row }) => (
                    <GridActions
                        editPermission={Permissions.AdminAccess}
                        editUrl={`/admin/agent-projects/edit/${requiredAgentProjectId(row)}`}
                    />
                ),
            },
        ];

        return columns;
    }, []);

    return agentColumns;
};

const Agents: FC = () => {
    const columns = useAgentLocationColumns();

    const theme = useTheme();

    const context = useAgentProjectSummaries();

    const { data: agentProjects, loading: agentProjectsLoading } = context;

    const exportMetadata = useMemo(
        () => ({
            fileName: `Agent Projects`,
            exportTitle: `Agent Projects`,
            includeDateTime: true,
        }),
        [],
    );

    return (
        <AgentProjectSummariesContext.Provider value={context}>
            <Outlet />
            <SetLinks createProjectLink={DefaultProjectCreateLink} stageLinkEnabled={false} lotLinkEnabled={false} />
            <Grid container gap={theme.spacing(2)} justifyContent={"end"} marginBottom={theme.spacing(2)}>
                <StyledButton
                    variant="contained"
                    //@ts-expect-error
                    component={Link}
                    to={`/admin/agent-personnel/create`}
                    state={{
                        back: true,
                    }}
                >
                    Add Agent Project
                </StyledButton>
            </Grid>
            <Paper>
                <Box>
                    <CollapsibleAppTable
                        density="compact"
                        startCollapsed={false}
                        title="Agent Projects"
                        loading={agentProjectsLoading}
                        rows={agentProjects ?? []}
                        columns={columns}
                        getRowId={requiredAgentProjectId}
                        columnControls={{
                            menu: true,
                        }}
                        filterControls
                        export={exportMetadata}
                    />
                </Box>
            </Paper>
        </AgentProjectSummariesContext.Provider>
    );
};

export default Agents;
